<template>
  <HeaderLanding />

  <main>
    <section class="hero">
      <AppWrapper>
        <div class="left">
          <h1 v-html="$t('demo-ootbi.hero.left.h1')" />
          <h3 v-html="$t('demo-ootbi.hero.left.h3')" />
          <p v-html="$t('demo-ootbi.hero.left.p')" />

          <div class="buttons">
            <Buttons.Anchor anchor="#form" fill="solid" accent="purple">
              {{ $t('Request_a_Demo') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Anchor>

            <Buttons.Regular
              fill="outline"
              accent="purple"
              data-id="hero-downloadAsset"
              @click="setFormState('getAsset', 'visible', true)"
            >
              {{ $t('Download_White_Paper') }}
              <SvgIcon name="download" />
            </Buttons.Regular>
          </div>
        </div>

        <div class="right">
          <NuxtImg
            src="/redesign/images/server/server-front-and-veeam-slogan.webp"
            alt=""
            width="605"
            height="272"
            sizes="xs:327px md:462px xl:605px"
            densities="x1 x2"
            format="webp"
            loading="lazy"
          />
        </div>
      </AppWrapper>
    </section>

    <PartnersMarquee />

    <div class="benefits">
      <AppWrapper>
        <ul class="cards">
          <li
            v-for="{ title, icon, listTitle, listItems } of BENEFITS"
            :key="title"
            class="card"
          >
            <strong>{{ title }}</strong>
            <SvgIcon :name="icon" />
            <p>{{ listTitle }}</p>

            <ul>
              <li v-for="item of listItems" :key="item">
                <span>{{ item }}</span>
              </li>
            </ul>
          </li>
        </ul>
      </AppWrapper>
    </div>

    <ThreeReasonsWhitePaper
      @download="setFormState('getAsset', 'visible', true)"
    />

    <section class="demo" id="form">
      <AppWrapper>
        <div class="left">
          <h2 v-html="$t('demo-ootbi.form.left.h2')" />

          <p v-html="$t('demo-ootbi.form.left.p')" />

          <ul>
            <li><p v-html="$t('demo-ootbi.form.left.li-1')" /></li>
            <li><p v-html="$t('demo-ootbi.form.left.li-2')" /></li>
            <li><p v-html="$t('demo-ootbi.form.left.li-3')" /></li>
          </ul>

          <strong v-html="$t('demo-ootbi.form.left.strong')" />
        </div>

        <div class="right">
          <Forms.Wrapper>
            <transition name="fade" mode="out-in">
              <div v-if="formsStates.requestDemo.submitted" class="thank-you">
                <strong>{{ $t('Thank_you_for_your_message') }}</strong>
                <p>{{ $t('We_already_working') }}.</p>

                <Buttons.Link to="/" accent="purple" target="_blank">
                  {{ $t('Visit_Home_Page') }}
                  <SvgIcon name="arrow-right-up" />
                </Buttons.Link>
              </div>

              <Forms.RequestDemo
                v-else
                :btn-text="$t('Request_a_Demo')"
                product="Object Storage"
                page="demo-ootbi"
                gtm-event="demo"
                @loading="setFormState('requestDemo', 'loading', $event)"
                @submitted="setFormState('requestDemo', 'submitted', true)"
              />
            </transition>
          </Forms.Wrapper>
        </div>
      </AppWrapper>
    </section>
  </main>

  <TheFooter />

  <ClientOnly>
    <Teleport to="#overlay">
      <Transition name="scale-fade">
        <Forms.Wrapper
          v-if="formsStates.getAsset"
          can-close
          @close="setFormState('getAsset', 'visible', false)"
        >
          <div class="top">
            <strong>
              {{
                formsStates.getAsset.submitted
                  ? $t('Download_Asset')
                  : $t('Provide_your_details_to_get_the_asset')
              }}
            </strong>

            <button
              type="button"
              @click="setFormState('getAsset', 'visible', false)"
            >
              <SvgIcon name="close" />
            </button>
          </div>

          <transition name="fade" mode="out-in">
            <Modals.DownloadAsset v-if="formsStates.getAsset.submitted" />

            <Forms.GetAsset
              v-else
              @loading="setFormState('getAsset', 'loading', $event)"
              @submitted="setFormState('getAsset', 'submitted', true)"
            />
          </transition>
        </Forms.Wrapper>
      </Transition>
    </Teleport>
  </ClientOnly>

  <AppLoader v-if="someFormLoading && !someFormSubmitted" />
</template>

<script setup lang="ts">
// components
import HeaderLanding from '~/components/redesign/HeaderLanding.vue';
import AppWrapper from '~/components/redesign/AppWrapper.vue';
import Buttons from '~/components/redesign/Buttons';
import PartnersMarquee from '~/components/redesign/PartnersMarquee.vue';
import Forms from '~/components/redesign/Forms';
import Modals from '~/components/redesign/Modals';
import AppLoader from '~/components/redesign/AppLoader.vue';
import TheFooter from '~/components/redesign/TheFooter.vue';
import ThreeReasonsWhitePaper from '~/components/redesign/ThreeReasonsWhitePaper.vue';

// composables
import useApiSeo from '~/composables/useApiSeo';

// stores
import useUIStore from '~/stores/ui';

import { useI18n } from '#i18n';

const { t } = useI18n();

const BENEFITS = [
  {
    title: t('Secure'),
    icon: 'lock-colorfull',
    listTitle: t('demo-ootbi.benefits-1.title'),
    listItems: [
      t('demo-ootbi.benefits-1.title.item-1'),
      t('demo-ootbi.benefits-1.title.item-2'),
      t('demo-ootbi.benefits-1.title.item-3'),
    ],
  },
  {
    title: t('Simple'),
    icon: 'like-colorfull',
    listTitle: t('demo-ootbi.benefits-2.title'),
    listItems: [
      t('demo-ootbi.benefits-2.title.item-1'),
      t('demo-ootbi.benefits-2.title.item-2'),
      t('demo-ootbi.benefits-2.title.item-3'),
    ],
  },
  {
    title: t('Powerful'),
    icon: 'rocket-colorfull',
    listTitle: t('demo-ootbi.benefits-3.title'),
    listItems: [
      t('demo-ootbi.benefits-3.title.item-1'),
      t('demo-ootbi.benefits-3.title.item-2'),
      t('demo-ootbi.benefits-3.title.item-3'),
    ],
  },
];

const formsStates = reactive({
  getAsset: {
    visible: false,
    loading: false,
    submitted: false,
  },
  requestDemo: {
    visible: false,
    loading: false,
    submitted: false,
  },
});
const setFormState = <
  N extends keyof typeof formsStates,
  S extends keyof (typeof formsStates)[N],
  V extends (typeof formsStates)[N][S],
>(
  formName: N,
  state: S,
  value: V,
) => {
  formsStates[formName][state] = value;
};

const { setOverlay } = useUIStore();

watchEffect(() => setOverlay({ visible: formsStates.getAsset.visible }));

const someFormLoading = computed(() =>
  Object.values(formsStates).some(({ loading }) => loading),
);
const someFormSubmitted = computed(() =>
  Object.values(formsStates).some(({ submitted }) => submitted),
);

definePageMeta({ layout: 'empty' });
useApiSeo('demo-ootbi');
</script>

<style scoped lang="scss">
@import '$/mixins/typography';
@import '$/mixins/size';
@import '$/mixins/flex';
@import '$/mixins/media';
@import '$/variables/shadows';

main {
  section.hero {
    @include flex-center-sb;
    gap: 0 2rem;
    padding: 8rem 0;

    @include tablet {
      gap: 3rem 0;
      flex-direction: column;
      padding: 4.8rem 0 2.7rem 0;
    }

    @include mobile {
      gap: 1.5rem 0;
      padding: 3.2rem 0 1.2rem 0;
    }

    .left {
      @include flex-start-start;
      flex-direction: column;
      gap: 2rem 0;
      @include fluid-max-width(37rem);

      @include tablet {
        gap: 1.5rem 0;
        max-width: 100%;
        text-align: center;
      }

      h1 {
        @include h3;
      }

      h3 {
        @include h4;

        @include tablet {
          margin: 0 auto;
        }
      }

      p {
        @include body-2;
      }

      .buttons {
        @include flex-center-sb;
        gap: 0 2rem;

        @include tablet {
          margin: 0 auto;
        }

        @include mobile {
          flex-direction: column;
          margin: 0;
          width: 100%;
          gap: 1.5rem 0;
        }

        [data-component-name='Buttons.Anchor'],
        [data-component-name='Buttons.Regular'] {
          @include mobile {
            width: 100%;
          }
        }
      }
    }

    .right {
      img {
        transform: translate(1rem, 1.4rem);

        @include tablet {
          transform: translate(0, 0);
          max-width: 28.8rem;
          height: auto;
        }

        @include mobile {
          width: calc(100% + 1.5rem);
          transform: translate(-0.75rem, 0);
        }
      }
    }
  }

  [data-component-name='PartnersMarquee'] {
    margin-bottom: 8rem;

    @include tablet {
      margin-bottom: 5rem;
    }
  }

  .benefits {
    margin-bottom: 8rem;

    @include tablet {
      margin-bottom: 5rem;
    }

    ul.cards {
      @include flex-stretch-start;
      gap: 0 2rem;

      @include tablet {
        flex-direction: column;
        gap: 1.5rem 0;
      }

      li.card {
        width: 100%;
        min-height: 24.7rem;

        @include flex-stretch-start;
        flex-direction: column;

        padding: 2rem;
        box-shadow: map-get($shadows, 'Elevation 1');

        @include tablet {
          padding: 1.5rem;
          min-height: auto;
        }

        @include mobile {
          padding: 1.5rem 1rem;
        }

        strong {
          @include subtitle-1;
          margin-bottom: 1.5rem;
        }

        svg {
          @include fixed-size(3.125rem);
          margin-bottom: 1.5rem;
        }

        p {
          @include body-2;
          margin-bottom: 0.5rem;
        }

        ul {
          padding-left: 1.6rem;

          li {
            position: relative;

            &:before {
              content: '';
              display: block;

              @include fixed-size(5.5px);

              position: absolute;
              top: 0.73rem;
              left: -0.92rem;

              background-color: token('text-primary');
              border-radius: 100px;
            }

            span {
              @include body-2;
            }
          }
        }
      }
    }
  }

  [data-component-name='ThreeReasonsWhitePaper'] {
    padding-bottom: 4rem;

    @include tablet {
      padding-bottom: 2.5rem;
    }
  }

  section.demo {
    @include flex-stretch-sb;
    gap: 0 2rem;
    padding-top: 4rem;
    padding-bottom: 8rem;

    @include tablet {
      flex-direction: column;
      padding-top: 2.5rem;
      padding-bottom: 5rem;
    }

    .left,
    .right {
      width: 50%;

      @include tablet {
        width: 100%;
      }
    }

    .left {
      @include tablet {
        margin-bottom: 2.5rem;
      }

      @include mobile {
        margin-bottom: 1.5rem;
      }

      h2 {
        @include h3;
        margin-bottom: 2rem;

        @include tablet {
          margin-bottom: 2.5rem;
        }

        @include mobile {
          margin-bottom: 1.5rem;
        }
      }

      ul {
        padding-left: 1.6rem;
        margin-bottom: 0.5rem;

        li {
          position: relative;

          &:before {
            content: '';
            display: block;

            @include fixed-size(5.5px);

            position: absolute;
            top: 0.73rem;
            left: -0.92rem;

            background-color: token('text-primary');
            border-radius: 100px;
          }

          p {
            margin: 0;
          }
        }
      }

      p {
        @include body-2;
        margin-bottom: 0.5rem;
      }

      strong {
        @include body-2;
        font-weight: 700;
      }
    }

    .right {
      @include tablet {
        @include fluid-max-width(37rem);
        margin: 0 auto;
      }

      .thank-you {
        height: 54.6rem;

        @include flex-center-center;
        flex-direction: column;
        gap: 1.5rem 0;

        @include tablet {
          min-height: 61rem;
        }

        strong {
          @include subtitle-1;
        }

        p {
          @include body-3;
        }
      }
    }
  }
}

[data-component-name='AppOverlay'] {
  @include flex-center-center;

  [data-component-name='Forms.Wrapper'] {
    @include fluid-max-width(38.75rem);

    .top {
      @include flex-center-sb;
      margin-bottom: 2rem;

      @include tablet {
        margin-bottom: 1.5rem;
      }

      @include mobile {
        margin-bottom: 1rem;
      }

      strong {
        @include subtitle-1;
        margin-right: 0.5rem;
      }
    }
  }
}
</style>
